import { z } from 'zod';
/**
 * Die Vertraege von `datev_export_rueckmeldung` — Migration `0188`, Chip
 * `DX-rueckkanal`.
 *
 * Spec `docs/superpowers/specs/2026-09-08-datev-export-design.md`, Abschnitt
 * 7.3 (`D-25`, `D-31`).
 *
 * **Zwei Vertraege, aus zwei verschiedenen Blickwinkeln auf dieselbe Zeile:**
 *
 * - `datevExportRueckmeldungZeileSchema` — die volle Zeile, so wie sie aus
 *   der Datenbank kommt (Lesevertrag).
 * - `datevExportRueckmeldungEingabeSchema` — was ein Mensch beim Erfassen
 *   angibt, OHNE die serverseitig gesetzten Felder (`id`, `gitSha`,
 *   `erfasstVon`, `erfasstAm`) und ohne `datevExportId` (kommt aus dem Pfad
 *   des jeweiligen Aufrufers, nicht aus dem Formularkoerper).
 *
 * **Diese Ära baut hier noch keinen Endpunkt** (Task 12 des Plans traegt
 * ausdruecklich nur Migration und Vertrag, "Faelle: keiner eigener"; ein
 * Erfassungsweg gehoert einem spaeteren Chip — voraussichtlich `DX-probelauf`
 * oder eine Erweiterung von `DX-maske`). Der Eingabevertrag steht trotzdem
 * schon hier, an der einen Stelle, die die Tabellenform kennt, damit der
 * spaetere Chip ihn uebernimmt statt ihn abzuschreiben.
 *
 * **Zwei verschiedene "Art"-Begriffe, absichtlich zwei Felder** (siehe
 * Kopfkommentar der Migration): `weg` ist der KANAL (Importprotokoll oder
 * Formular, Spec 7.3), `ergebnis` ist der BEFUND der Rueckmeldung
 * (importiert/abgelehnt/teilweise/korrektur).
 *
 * **`anzahlKorrekturen` ist die Messgroesse des Nordsterns** — "Anzahl seiner
 * Korrekturen -> 0". Sie steht auf jeder Zeile, auch bei `ergebnis =
 * 'importiert'` ohne Beanstandung (dort `0`).
 *
 * **Keine Buchungswirkung** (`D-31`): Der Vertrag traegt bewusst kein Feld,
 * das auf `journal_entry`/`journal_line` verweist. `satzbezug` ist Freitext.
 */
/** Ein Datum in ISO-Form (`JJJJ-MM-TT`), so wie es aus `date`-Spalten kommt. */
const isoDatumSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet wird ein Datum JJJJ-MM-TT.');
/** Ein Zeitstempel in ISO-Form, so wie `pg` ihn fuer `timestamptz` liefert. */
const isoZeitstempelSchema = z.string().min(1);
/**
 * Der KANAL, auf dem die Rueckmeldung ankam (Spec 7.3, `D-25`).
 *
 * Weg 2 (der korrigierte Stapel zurueck) ist eine benannte Grenze und kommt
 * bewusst nicht als dritter Wert vor — siehe Kopfkommentar der Migration.
 */
export const datevExportRueckmeldungWegSchema = z.enum(['importprotokoll', 'formular']);
/**
 * Das ERGEBNIS der Rueckmeldung, unabhaengig vom Kanal (`weg`).
 *
 * `korrektur` deckt eine gemeldete Aenderung ohne vollstaendige Ablehnung des
 * Stapels.
 */
export const datevExportRueckmeldungErgebnisSchema = z.enum([
    'importiert',
    'abgelehnt',
    'teilweise',
    'korrektur',
]);
/**
 * Die vier Formularfelder aus Spec 7.3 sowie der Protokollauszug und der
 * Archivstueck-Verweis — gemeinsam definiert, weil Lese- und Eingabevertrag
 * dieselben Regeln fuer sie brauchen.
 */
const gemeinsameFelder = {
    zeitraumVon: isoDatumSchema.nullable(),
    zeitraumBis: isoDatumSchema.nullable(),
    satzbezug: z.string().min(1).nullable(),
    wasGeaendert: z.string().min(1).nullable(),
    begruendung: z.string().min(1).nullable(),
    protokollAuszug: z.string().min(1).nullable(),
    protokollPdfObjectKey: z.string().min(1).nullable(),
    protokollPdfSha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/, 'Erwartet werden 64 hexadezimale Zeichen.')
        .nullable(),
};
/** Beide gesetzt oder beide leer — dieselbe Bedingung wie der CHECK in der Migration. */
function pruefePdfPaar(wert, ctx) {
    const objectKeyGesetzt = wert.protokollPdfObjectKey !== null;
    const sha256Gesetzt = wert.protokollPdfSha256 !== null;
    if (objectKeyGesetzt !== sha256Gesetzt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'protokollPdfObjectKey und protokollPdfSha256 müssen beide gesetzt oder beide leer sein.',
            path: ['protokollPdfObjectKey'],
        });
    }
}
/** `zeitraumBis` darf nicht vor `zeitraumVon` liegen, wenn beide gesetzt sind. */
function pruefeZeitraumReihenfolge(wert, ctx) {
    if (wert.zeitraumVon !== null && wert.zeitraumBis !== null && wert.zeitraumBis < wert.zeitraumVon) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'zeitraumBis darf nicht vor zeitraumVon liegen.',
            path: ['zeitraumBis'],
        });
    }
}
/**
 * Eine Zeile der Tabelle `datev_export_rueckmeldung` — der Lesevertrag.
 */
export const datevExportRueckmeldungZeileSchema = z
    .object({
    id: z.string().uuid(),
    datevExportId: z.string().uuid(),
    weg: datevExportRueckmeldungWegSchema,
    ergebnis: datevExportRueckmeldungErgebnisSchema,
    anzahlKorrekturen: z.number().int().min(0),
    datumRueckmeldung: isoDatumSchema,
    ...gemeinsameFelder,
    gitSha: z.string().regex(/^[0-9a-f]{40}$/, 'Erwartet werden 40 hexadezimale Zeichen.'),
    erfasstVon: z.string().uuid(),
    erfasstAm: isoZeitstempelSchema,
})
    .strict()
    .superRefine((wert, ctx) => {
    pruefePdfPaar(wert, ctx);
    pruefeZeitraumReihenfolge(wert, ctx);
});
/**
 * Was ein Mensch beim Erfassen einer Rueckmeldung angibt.
 *
 * Ohne `id`, `gitSha`, `erfasstVon`, `erfasstAm` (serverseitig gesetzt) und
 * ohne `datevExportId` (kommt aus dem Pfad des Aufrufers).
 */
export const datevExportRueckmeldungEingabeSchema = z
    .object({
    weg: datevExportRueckmeldungWegSchema,
    ergebnis: datevExportRueckmeldungErgebnisSchema,
    anzahlKorrekturen: z.number().int().min(0),
    datumRueckmeldung: isoDatumSchema,
    ...gemeinsameFelder,
})
    .strict()
    .superRefine((wert, ctx) => {
    pruefePdfPaar(wert, ctx);
    pruefeZeitraumReihenfolge(wert, ctx);
});
//# sourceMappingURL=rueckmeldung.js.map